/**
 * NeedsTagBadges — compact display of a client's drill-library needs tags.
 *
 * A distinct visual tier from primary classification badges and free-form custom
 * tags: smaller, taxonomy-colored pills with a thin ring. Used on the roster and
 * anywhere a condensed needs summary is helpful.
 */

import { useEffect, useState } from 'react';
import type { ClientNeedsTag } from '@/lib/types';
import { needsTagKey } from '@/lib/clients/needs-tags';
import { loadColors, getTagColor, colorIsLight } from '@/utils/drill-ui';
import { cn } from '@/lib/utils';

interface NeedsTagBadgesProps {
  needsTags?: ClientNeedsTag[];
  max?: number;
  className?: string;
}

export function NeedsTagBadges({ needsTags, max = 3, className }: NeedsTagBadgesProps) {
  const [colorsReady, setColorsReady] = useState(false);

  useEffect(() => {
    let active = true;
    void loadColors().finally(() => {
      if (active) setColorsReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  if (!needsTags || needsTags.length === 0) return null;

  const shown = needsTags.slice(0, max);
  const extra = needsTags.length - shown.length;

  return (
    <div className={cn('flex flex-wrap gap-1', className)} data-testid="client-needs-badges">
      {shown.map((tag) => {
        const color = colorsReady ? getTagColor(tag.category, tag.value) : '#64748B';
        return (
          <span
            key={needsTagKey(tag)}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ring-black/15"
            style={{ backgroundColor: color, color: colorIsLight(color) ? '#0f172a' : '#ffffff' }}
            title={`${tag.category}: ${tag.value}`}
          >
            {tag.value}
          </span>
        );
      })}
      {extra > 0 && (
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          +{extra}
        </span>
      )}
    </div>
  );
}
